import React from 'react';

const META = {
	lastUpdated:  'September 23, 2026',
	effective:    'June 1, 2026',
	email:        'privacy@justknow.co.in',
	dpoEmail:     'contact@justknow.co.in',
	company:      'JGS Media and Software Solutions Private Limited',
};

const ReporterPrivacyPolicyPage: React.FC = () => (
	<div style={{
		position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
		overflowY: 'auto', background: '#0a0a1a',
		fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
		color: '#fff',
		zIndex: 9000,
	}}>

		{/* Nav */}
		<nav style={{
			position: 'sticky', top: 0, zIndex: 100,
			background: 'rgba(10,10,26,0.9)', backdropFilter: 'blur(20px)',
			borderBottom: '1px solid rgba(255,255,255,0.06)',
			padding: '0 24px',
		}}>
			<div style={{ maxWidth: 800, margin: '0 auto', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
				<a href='/'>
					<img src='/HeaderLogo2.png' alt='Justknow' style={{ height: 32, objectFit: 'contain' }} />
				</a>
				<a href='/' style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, textDecoration: 'none' }}>← Back to Home</a>
			</div>
		</nav>

		{/* Content */}
		<div style={{ maxWidth: 800, margin: '0 auto', padding: '56px 24px 80px' }}>

			{/* Header */}
			<div style={{ marginBottom: 48 }}>
				<div style={{ display: 'inline-block', background: 'rgba(77,105,250,0.12)', border: '1px solid rgba(77,105,250,0.3)', borderRadius: 6, padding: '4px 12px', fontSize: 12, color: '#4d69fa', fontWeight: 600, marginBottom: 16 }}>
					Just Know Reporter App
				</div>
				<h1 style={{ fontSize: 36, fontWeight: 800, letterSpacing: '-1px', marginBottom: 12 }}>Privacy Policy</h1>
				<p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 14 }}>
					Effective {META.effective} &nbsp;·&nbsp; Last updated {META.lastUpdated}
				</p>
				<p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 15, lineHeight: 1.7, marginTop: 20 }}>
					This Privacy Policy explains how <strong style={{ color: '#fff' }}>{META.company}</strong> collects, uses, stores, shares, and protects your personal data when you use the <strong style={{ color: '#fff' }}>Just Know Reporter</strong> app — including sensitive KYC and financial information specific to the reporter role. By registering and using the Reporter App you consent to these practices.
				</p>
				<p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 13, marginTop: 12 }}>
					Compliant with DPDPA 2023 · IT Act 2000 · IT Rules 2021 · PMLA 2002 · Indian Contract Act 1872
				</p>
			</div>

			<Divider />

			<Section title="1. Data Fiduciary Information">
				<p>As required under DPDPA 2023 (Section 8), we identify ourselves as the Data Fiduciary:</p>
				<InfoCard
					headline="Just Know Reporter"
					lines={[
						'Registered Office: JGS Media and Software Solutions Pvt Ltd, Dr.No 9-10-184, 23rd Ward, Ramakrishna Colony, Tuni, Kakinada District, Andhra Pradesh – 533401, India',
						'Email: privacy@justknow.co.in',
						'Data Protection Officer: JGS Srinivasa Rao',
						'DPO Email: contact@justknow.co.in',
						'Response Time: Within 30 days',
					]}
				/>
			</Section>

			<Section title="2. Personal Data We Collect">
				<p>We collect only what is necessary to operate the reporter platform:</p>

				<SubSection title="2.1 Registration Information">
					<p>When you create a reporter account we collect your name, email address, phone number, date of birth, and preferred language to set up and manage your profile.</p>
				</SubSection>

				<SubSection title="2.2 Identity Verification Documents (KYC)">
					<p>To verify your identity and enable payouts, we collect government-issued identity and address proof documents (such as Aadhaar, PAN, or Voter ID) along with a recent photograph. These are handled with strict confidentiality and accessed only by authorised personnel.</p>
				</SubSection>

				<SubSection title="2.3 Payment & Financial Information">
					<p>To process your earnings we collect bank account details or digital payment information you provide. This data is used solely for transferring your rewards and is not shared for any other purpose. Financial records are retained as required by applicable Indian law including PMLA 2002.</p>
				</SubSection>

				<SubSection title="2.4 Location Information">
					<p>We collect the location details you provide at registration — state, district, and area — to assign you relevant regional news coverage. <strong style={{ color: '#fff' }}>We do not track your real-time or GPS location.</strong></p>
				</SubSection>

				<SubSection title="2.5 Content & Activity Data">
					<p>We collect the articles, photos, and videos you submit through the app, along with your submission history and rewards activity, to operate the reporter platform and maintain your account record.</p>
				</SubSection>

				<SubSection title="2.6 Device & Technical Data">
					<p>We collect basic technical information including device model, OS version, IP address, app usage logs, and FCM push token (for notifications, with your consent) to ensure the platform functions correctly and to maintain security.</p>
				</SubSection>
			</Section>

			<Section title="3. How We Use Your Personal Data">
				<SubSection title="3.1 Core Platform Services">
					<p>We use your data to operate your reporter account, verify your identity, process article submissions, manage rewards, and send you essential account-related notifications.</p>
				</SubSection>

				<SubSection title="3.2 Editorial Operations">
					<p>Your location and language preferences help us match you with relevant regional news assignments and keep you informed of editorial feedback and opportunities in your area.</p>
				</SubSection>

				<SubSection title="3.3 Security & Legal Compliance">
					<p>We use your data to protect the platform from misuse and to meet our obligations under applicable Indian laws, including those related to identity verification and financial reporting.</p>
				</SubSection>

				<SubSection title="3.4 Communications">
					<p>With your consent, we may send you notifications about article updates, new assignment opportunities, platform changes, and account security alerts. You may opt out at any time from your notification settings.</p>
				</SubSection>
			</Section>

			<Section title="4. How We Share Your Data">
				<p><strong style={{ color: '#fff' }}>We do not sell, rent, or trade your personal data.</strong> Sharing occurs only in the following limited circumstances:</p>

				<SubSection title="4.1 Trusted Service Providers">
					<p>We work with trusted third-party providers to operate our platform, including cloud hosting (AWS), push notifications (Firebase), and payment processing. These partners receive only the minimum data necessary to perform their specific function and are contractually bound to handle it securely.</p>
				</SubSection>

				<SubSection title="4.2 Legal & Regulatory Requirements">
					<p>We may disclose your personal data to government or regulatory authorities when required to do so by applicable law, court order, or official legal process.</p>
				</SubSection>

				<SubSection title="4.3 Published Content (Public Information)">
					<p>When your articles are published on Justknow, the following information becomes publicly visible:</p>
					<ul>
						<li>Your display name / byline and profile picture</li>
						<li>Your article text, photos, and videos</li>
						<li>Your state and district (regional attribution)</li>
					</ul>
					<p style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.4)', marginTop: 8 }}>
						Your email, phone number, financial data, and KYC documents are never made public under any circumstances.
					</p>
				</SubSection>
			</Section>

			<Section title="5. Data Retention">
				<p>We retain your personal data only for as long as necessary to provide our services and to meet our legal obligations under applicable Indian law, including statutory requirements related to KYC, financial records (minimum 5 years under PMLA 2002), and journalistic content. Once data is no longer required for these purposes, it is securely deleted or anonymised.</p>
			</Section>

			<Section title="6. Your Rights (DPDPA 2023)">
				<p>Under the Digital Personal Data Protection Act 2023, you have the right to:</p>
				<ul>
					<li><b>Access</b> — request a copy of the personal data we hold about you.</li>
					<li><b>Correction</b> — request that inaccurate or incomplete data be corrected.</li>
					<li><b>Erasure</b> — request deletion of your data (subject to legal retention requirements including KYC and financial records).</li>
					<li><b>Data portability</b> — receive your data in a machine-readable format.</li>
					<li><b>Withdraw consent</b> — opt out of optional processing such as marketing notifications at any time.</li>
					<li><b>Grievance redressal</b> — file a complaint and receive acknowledgement within 48 hours, with resolution within 30 days.</li>
				</ul>
				<p>To exercise any of these rights, email us at <a href={`mailto:${META.email}`} style={{ color: '#4d69fa' }}>{META.email}</a> with the subject <em>Data Rights Request</em>.</p>
			</Section>

			<Section title="7. Security Measures">
				<p>We implement appropriate technical and organisational measures to protect your data against unauthorised access, disclosure, alteration, or destruction — including encryption in transit (TLS 1.3), encrypted storage for sensitive KYC data (AES-256), role-based access controls, and regular security audits. Access to KYC and financial data is strictly limited to authorised personnel. In the event of a data breach affecting your data, we will notify you promptly in accordance with applicable law.</p>
			</Section>

			<Section title="8. Minimum Age Requirement">
				<p>The Reporter App is strictly for users <strong style={{ color: '#fff' }}>18 years of age and above</strong>, as required by the Indian Contract Act, 1872. We verify age via the date of birth provided at registration. We do not knowingly collect data from anyone under 18. If you believe a minor has registered, contact us immediately at <a href={`mailto:${META.email}`} style={{ color: '#4d69fa' }}>{META.email}</a>.</p>
			</Section>

			<Section title="9. Changes to This Policy">
				<p>We may update this Privacy Policy periodically. When we make material changes, we will notify you via in-app notification and email to your registered address. The updated policy takes effect on the date shown at the top of this page. Continued use after the effective date constitutes acceptance of the revised policy.</p>
			</Section>

			<Divider />

			{/* Footer contact */}
			<div style={{ textAlign: 'center', paddingTop: 8 }}>
				<p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 14 }}>
					Questions? Email us at{' '}
					<a href={`mailto:${META.email}`} style={{ color: '#4d69fa' }}>{META.email}</a>
				</p>
			</div>
		</div>

		{/* Footer */}
		<footer style={{ borderTop: '1px solid rgba(255,255,255,0.06)', background: 'rgba(0,0,0,0.3)', padding: '32px 24px' }}>
			<div style={{ maxWidth: 800, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' as const, gap: 16 }}>
				<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
					<img src='/HeaderLogo2.png' alt='Justknow' style={{ height: 26, objectFit: 'contain' }} />
					<span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11 }}>Reporter App</span>
				</div>
				<span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>
					© {new Date().getFullYear()} {META.company}. All rights reserved.
				</span>
			</div>
		</footer>

		<style>{`
			* { margin: 0; padding: 0; box-sizing: border-box; }
			::-webkit-scrollbar { width: 6px; }
			::-webkit-scrollbar-track { background: #0a0a1a; }
			::-webkit-scrollbar-thumb { background: rgba(108,93,211,0.5); border-radius: 3px; }
		`}</style>
	</div>
);

// ─── helpers ──────────────────────────────────────────────────────────────────

const Divider = () => (
	<hr style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.06)', margin: '40px 0' }} />
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
	<div style={{ marginBottom: 40 }}>
		<h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 14, color: '#fff' }}>{title}</h2>
		<div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 15, lineHeight: 1.8 }}>
			{children}
		</div>
		<style>{`
			div > div ul { padding-left: 20px; margin: 8px 0; }
			div > div li { margin-bottom: 8px; }
			div > div p  { margin-bottom: 12px; }
		`}</style>
	</div>
);

const SubSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
	<div style={{ marginTop: 20 }}>
		<h3 style={{ fontSize: 15, fontWeight: 700, color: 'rgba(255,255,255,0.8)', marginBottom: 8 }}>{title}</h3>
		{children}
	</div>
);

const InfoCard: React.FC<{ headline: string; lines: string[] }> = ({ headline, lines }) => (
	<div style={{
		background: 'rgba(255,255,255,0.04)',
		border: '1px solid rgba(255,255,255,0.08)',
		borderRadius: 10,
		padding: '16px 20px',
		marginTop: 12,
	}}>
		<div style={{ fontWeight: 700, color: '#4d69fa', marginBottom: 8 }}>{headline}</div>
		{lines.map((line, i) => (
			<div key={i} style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', lineHeight: 1.7 }}>{line}</div>
		))}
	</div>
);

export default ReporterPrivacyPolicyPage;
